import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import OnboardingLayout from "./Onboarding/OnboardingLayout";
import Input from "./ui/Input";
import { getInstagramAuthUrl } from "../utils/instagramAuth";

const BrandOnBoardingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const totalSteps = 5;

  const [formData, setFormData] = useState({
    brandName: "",
    website: "",
    instagram: "",
    bio: "",
    logo: null
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});



  // Autofill from Auth
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.displayName && !formData.brandName) {
        setFormData(prev => ({ ...prev, brandName: user.displayName }));
      }
    });
    return () => unsubscribe();
  }, [formData.brandName]);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ ...prev, logo: file }));

      const objectUrl = URL.createObjectURL(file);
      setLogoPreview(objectUrl);

      if (errors.logo) {
        setErrors(prev => ({ ...prev, logo: "" }));
      }
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    let isValid = true;

    switch (currentStep) {
      case 1:
        if (!formData.brandName.trim()) {
          newErrors.brandName = "Brand Name is required";
          isValid = false;
        }
        break;
      case 2:
        if (!formData.website.trim()) {
          newErrors.website = "Website is required";
          isValid = false;
        }
        else if (!/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.website)) {
          newErrors.website = "Please enter a valid URL";
          isValid = false;
        }
        break;
      case 3:
        if (!formData.instagram || !formData.instagram.trim()) {
          newErrors.instagram = "Instagram link is required";
          isValid = false;
        } else {
          const instaRegex = /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?$/;
          if (!instaRegex.test(formData.instagram.trim())) {
            newErrors.instagram = "Please enter a valid Instagram profile link";
            isValid = false;
          }
        }
        break;
      case 4:
        if (!formData.bio.trim()) {
          newErrors.bio = "Bio is required";
          isValid = false;
        }
        break;
      case 5:
        if (!formData.logo) {
          newErrors.logo = "Please upload a logo";
          isValid = false;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;

    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      // Final Submission
      const submitData = async () => {
        try {
          const user = auth.currentUser;
          if (!user) {
            console.error("No user logged in");
            return;
          }

          // In a real app, upload image to Storage and get URL first.
          // For now, we skip the file object or store a placeholder.

          await setDoc(doc(db, "brands", user.uid), {
            uid: user.uid,
            email: user.email,
            brandName: formData.brandName,
            website: formData.website,
            instagram: formData.instagram,
            bio: formData.bio,
            // logo: formData.logo, // Cannot save File object directly to Firestore
            role: "brand",
            createdAt: new Date().toISOString()
          });

          console.log("Brand Data Saved to Firestore");
          navigate("/brand/dashboard");
        } catch (error) {
          console.error("Error saving brand data:", error);
          // Handle error (show toast etc)
        }
      };
      submitData();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNext();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate(-1);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Input
            name="brandName"
            value={formData.brandName}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            error={errors.brandName}
            placeholder="Enter Brand Name"
          />
        );
      case 2:
        return (
          <Input
            name="website"
            value={formData.website}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            error={errors.website}
            placeholder="www.example.com"
          />
        );
      case 3:
        return (
          <div className="flex flex-col items-center justify-center py-4">
             <div className="w-20 h-20 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 rounded-2xl flex items-center justify-center text-white text-4xl mb-6 shadow-lg animate-float-mid">
                <i className="fab fa-instagram"></i>
             </div>
             <p className="text-center text-gray-500 mb-8 max-w-sm font-medium">
                Paste your Brand's Instagram profile link to help creators discover you.
             </p>
             <div className="w-full max-w-md">
                <Input
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  error={errors.instagram}
                  placeholder="instagram.com/brandname"
                />
             </div>
             <p className="text-[10px] text-gray-400 mt-4 text-center">
                Example: https://www.instagram.com/brandname
             </p>
          </div>
        );
      case 4:
        return (
          <>
            <div className="relative">
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleNext();
                  }
                }}
                maxLength={160}
                placeholder="Bio"
                className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-800 placeholder-gray-400 min-h-[120px] transition-all pb-8 ${errors.bio ? 'border-red-500 focus:ring-red-200' : 'border-gray-200'}`}
              />
              <span className="absolute bottom-3 right-4 text-xs text-gray-400 font-medium">
                {formData.bio.length}/160
              </span>
            </div>
            {errors.bio && <p className="text-red-500 text-xs mt-1 ml-1">{errors.bio}</p>}
          </>
        );
      case 5:
        return (
          <div className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 bg-white transition-colors relative h-[300px] overflow-hidden ${errors.logo ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}>
            {logoPreview ? (
              <div className="flex flex-col items-center w-full">
                <div className="w-24 h-24 mb-3 rounded-full overflow-hidden border-4 border-white shadow-lg shrink-0">
                  <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-cover" />
                </div>
                <p className="text-green-600 font-medium mb-1 text-sm">Looks great!</p>
                <p className="text-xs text-gray-500 mb-3 truncate max-w-[200px]">{formData.logo.name}</p>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="logo-upload-change"
                />
                <label htmlFor="logo-upload-change" className="cursor-pointer text-xs text-blue-600 font-medium hover:underline bg-blue-50 px-3 py-1.5 rounded-lg">Change Logo</label>
              </div>
            ) : (
              <>
                <div className="text-blue-500 text-3xl mb-3">
                  <i className="fa-regular fa-image"></i>
                </div>
                <p className="text-gray-900 font-bold mb-1 text-base">Drag & Drop image</p>
                <p className="text-gray-400 text-xs mb-3">Or browse file on your computer</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload" className="cursor-pointer text-blue-600 font-medium hover:underline">Browse</label>
              </>
            )}
            {errors.logo && !logoPreview && <p className="text-red-500 text-xs mt-2 font-bold">{errors.logo}</p>}
          </div>
        );
      default:
        return null;
    }
  };

  const stepTitles = {
    1: "What is the name of your brand?",
    2: "Link to your website",
    3: "Link to your instagram",
    4: "Write a brief bio for your brand.",
    5: "Please upload your brand logo."
  };

  const stepSubtitles = {
    1: "What name are you known by?",
    2: "This helps creators identify your brand.",
    3: "This helps creators discover your brand.",
    4: "This helps creators understand your brand's identity.",
    5: "This helps creators recognize your brand visually."
  };

  const getPreviewContent = () => {
    return (
      <div className="flex flex-col h-full bg-slate-50 relative animate-in fade-in duration-500 font-sans">
        {/* Top Status Bar UI */}
        <div className="h-6 w-full flex justify-between items-center px-6 pt-2">
          <div className="text-[10px] font-bold">9:41</div>
          <div className="flex gap-1">
            <div className="w-4 h-2 bg-black rounded-sm opacity-20"></div>
            <div className="w-2 h-2 bg-black rounded-full opacity-20"></div>
          </div>
        </div>

        {/* Brand Profile Preview */}
        <div className="p-6 pt-8">
          <div className="bg-white rounded-3xl shadow-xl shadow-blue-500/5 overflow-hidden border border-slate-100 flex flex-col">
            {/* Cover Placeholder */}
            <div className="h-20 w-full bg-gradient-to-r from-blue-600 to-indigo-600 relative">
               <div className="absolute inset-0 bg-white/10 backdrop-blur-sm animate-pulse"></div>
            </div>

            <div className="px-5 pb-6 -mt-8 relative z-10 flex flex-col items-start">
              {/* Logo Preview */}
              <div className="w-20 h-20 rounded-2xl bg-white p-1 border-4 border-white shadow-lg mb-4 overflow-hidden flex items-center justify-center transition-all">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <div className="w-full h-full bg-slate-50 flex items-center justify-center text-slate-200">
                     <i className="fa-solid fa-building text-3xl"></i>
                  </div>
                )}
              </div>

              {/* Brand Name */}
              <h4 className={`text-xl font-black text-slate-900 mb-1 min-h-[1.5rem] ${!formData.brandName ? 'w-32 h-4 bg-slate-100 rounded-full animate-pulse' : ''}`}>
                {formData.brandName || ""}
              </h4>
              
              {/* Website Link */}
              <div className={`text-blue-600 text-[10px] font-bold mb-4 flex items-center gap-1.5 min-h-[1rem] ${!formData.website ? 'w-24 h-2 bg-blue-50 rounded-full' : ''}`}>
                {formData.website && (
                  <>
                    <i className="fa-solid fa-link text-[8px]"></i>
                    {formData.website.replace(/^https?:\/\//, '').split('/')[0]}
                  </>
                )}
              </div>

              {/* Bio */}
              <p className={`text-[10px] text-slate-500 font-medium leading-relaxed mb-6 min-h-[2.5rem] ${!formData.bio ? 'w-full h-3 bg-slate-50 rounded-full mb-1' : ''}`}>
                {formData.bio || (formData.brandName ? "Your brand bio will appear here..." : "")}
              </p>

              {/* Social Buttons Mockup */}
              <div className="flex gap-2 w-full">
                 <div className={`flex-1 h-8 rounded-lg flex items-center justify-center gap-2 text-[10px] font-bold ${formData.instagram ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-300'}`}>
                    <i className="fa-brands fa-instagram"></i>
                    {formData.instagram ? "Linked" : "Instagram"}
                 </div>
                 <div className="flex-1 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white text-[10px] font-bold">
                    Follow
                 </div>
              </div>
            </div>
          </div>

          {/* Campaign Stats Mockup */}
          <div className="mt-6">
             <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3 px-1">Active Campaigns</div>
             <div className="space-y-2">
                <div className="h-14 w-full bg-white rounded-2xl border border-slate-50 shadow-sm flex items-center px-4 gap-3">
                   <div className="w-8 h-8 bg-slate-50 rounded-lg"></div>
                   <div className="flex-1 space-y-1.5">
                      <div className="w-20 h-2 bg-slate-100 rounded-full"></div>
                      <div className="w-12 h-1.5 bg-slate-50 rounded-full"></div>
                   </div>
                </div>
             </div>
          </div>
        </div>

        {/* Brand Tab Bar Mock */}
        <div className="mt-auto h-16 border-t border-slate-100 bg-white flex items-center justify-around px-10">
           <div className="w-6 h-6 text-blue-600">
              <i className="fa-solid fa-chart-pie"></i>
           </div>
           <div className="w-6 h-6 text-slate-200">
              <i className="fa-solid fa-bolt"></i>
           </div>
           <div className="w-6 h-6 text-slate-200">
              <i className="fa-solid fa-gear"></i>
           </div>
        </div>
      </div>
    );
  };

  return (
    <OnboardingLayout
      currentStep={step}
      totalSteps={totalSteps}
      title={stepTitles[step]}
      subtitle={stepSubtitles[step]}
      onBack={handleBack}
      onContinue={handleNext}
      continueLabel={step === totalSteps ? "Submit" : "Continue"}
      preview={getPreviewContent()}
    >
      {renderStep()}
    </OnboardingLayout>
  );
};

export default BrandOnBoardingPage;
